import { useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Switch, FormControlLabel, Chip, IconButton, Stack, Alert, MenuItem, Tooltip,
  InputAdornment, Grid, CircularProgress, Divider,
} from '@mui/material';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import {
  Add, Settings, Visibility, Edit, PauseCircle, PlayCircle, Block, Delete, Search,
} from '@mui/icons-material';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import DataTableCard from '../../components/ui/DataTableCard';
import EnhancedDataGrid from '../../components/ui/EnhancedDataGrid';
import QueryErrorAlert from '../../components/ui/QueryErrorAlert';
import TenantFormDrawer from '../../components/tenants/TenantFormDrawer';
import TenantViewDialog from '../../components/tenants/TenantViewDialog';
import { usePlatformServices } from '../../hooks/useServiceCatalog';
import { refreshTenantSession } from '../../hooks/useTenantBootstrap';
import { countEnabledTenantServiceModules, filterTenantServiceModules } from '../../constants/tenantModules';
import {
  listTenants, createTenant, updateTenant, updateTenantModules, updateTenantStatus, deleteTenant,
  upsertTenantAdmin, getTenant,
  TENANT_STATUS_META, type TenantFormPayload, type TenantRow, type TenantStatus,
} from '../../services/tenantService';
import type { RootState } from '../../store';

function extractError(err: unknown) {
  const e = err as { response?: { data?: { message?: string } } };
  return e.response?.data?.message || 'Something went wrong';
}

export default function TenantsPage() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const currentTenantId = useSelector((s: RootState) => s.tenant.id);
  const { data: platformServices = [] } = usePlatformServices();
  const serviceModules = useMemo(() => filterTenantServiceModules(platformServices), [platformServices]);

  const syncSessionIfCurrent = async (tenantId: string) => {
    if (currentTenantId && currentTenantId === tenantId) {
      await refreshTenantSession(dispatch);
    }
  };

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<TenantStatus | 'all'>('all');
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selected, setSelected] = useState<TenantRow | null>(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [formError, setFormError] = useState('');

  const [moduleDialog, setModuleDialog] = useState<{
    open: boolean;
    tenant: TenantRow | null;
    draftModules: { key: string; enabled: boolean }[];
  }>({ open: false, tenant: null, draftModules: [] });
  const modulesHydratedForId = useRef<string | null>(null);

  const openModulesDialog = (tenant: TenantRow) => {
    modulesHydratedForId.current = null;
    setModuleDialog({
      open: true,
      tenant,
      draftModules: (tenant.modules || []).map((m) => ({ key: m.key, enabled: !!m.enabled })),
    });
  };

  const closeModulesDialog = () => {
    modulesHydratedForId.current = null;
    setModuleDialog({ open: false, tenant: null, draftModules: [] });
  };

  const moduleTenantId = moduleDialog.open ? moduleDialog.tenant?._id : undefined;
  const { data: freshModuleTenant, isLoading: modulesLoading, isError: modulesError, refetch: refetchModules } = useQuery({
    queryKey: ['tenant', moduleTenantId, 'settings'],
    queryFn: () => getTenant(moduleTenantId!),
    enabled: !!moduleTenantId,
    staleTime: 15_000,
  });

  useEffect(() => {
    if (!moduleDialog.open || !freshModuleTenant) return;
    if (moduleDialog.tenant?._id !== freshModuleTenant._id) return;
    if (modulesHydratedForId.current === freshModuleTenant._id) return;
    modulesHydratedForId.current = freshModuleTenant._id;
    setModuleDialog((prev) => ({
      ...prev,
      tenant: {
        ...prev.tenant!,
        companyName: freshModuleTenant.companyName,
        slug: freshModuleTenant.slug,
        modules: freshModuleTenant.modules,
      },
      draftModules: (freshModuleTenant.modules || []).map((m) => ({ key: m.key, enabled: !!m.enabled })),
    }));
  }, [moduleDialog.open, moduleDialog.tenant?._id, freshModuleTenant]);
  const [confirm, setConfirm] = useState<{ open: boolean; action: TenantStatus | 'delete'; tenant: TenantRow | null }>({
    open: false, action: 'suspended', tenant: null,
  });
  const [mutationError, setMutationError] = useState('');

  const queryKey = ['tenants', pagination.page, pagination.pageSize, debouncedSearch, statusFilter];

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () => listTenants({
      page: pagination.page + 1,
      limit: pagination.pageSize,
      search: debouncedSearch || undefined,
      status: statusFilter,
    }),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['tenants'] });

  const buildModulesPayload = (payload: TenantFormPayload, tenant?: TenantRow | null) => {
    const existing = new Map((tenant?.modules || []).map((m) => [m.key, m.enabled]));
    const merged = platformServices.map((m) => {
      const isService = serviceModules.some((s) => s.code === m.code);
      return {
        key: m.code,
        // Service modules come from the form; platform add-ons (wallet/loyalty/subscriptions) are preserved.
        enabled: isService
          ? payload.enabledModules.includes(m.code)
          : (existing.get(m.code) ?? false),
      };
    });
    tenant?.modules?.filter((m) => !platformServices.some((p) => p.code === m.key)).forEach((m) => {
      merged.push({ key: m.key, enabled: m.enabled });
    });
    return merged;
  };

  const createMut = useMutation({
    mutationFn: async (payload: TenantFormPayload) => {
      const res = await createTenant(payload);
      const tenantId = res?.data?.tenant?._id;
      if (!tenantId) throw new Error('Tenant was created but no id was returned');
      // Persist exact module selection (same merge rules as edit / Modules dialog).
      await updateTenantModules(tenantId, buildModulesPayload(payload));
      return res;
    },
    onSuccess: () => { invalidate(); setFormOpen(false); setFormError(''); },
    onError: (err) => setFormError(extractError(err)),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<TenantFormPayload> }) => updateTenant(id, payload),
    onSuccess: invalidate,
  });

  const modulesMut = useMutation({
    mutationFn: ({ id, modules }: { id: string; modules: { key: string; enabled: boolean }[] }) => updateTenantModules(id, modules),
    onSuccess: async (_data, vars) => {
      invalidate();
      await syncSessionIfCurrent(vars.id);
      void queryClient.invalidateQueries({ queryKey: ['tenant', vars.id] });
      closeModulesDialog();
    },
    onError: (err) => setMutationError(extractError(err)),
  });

  const statusMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: TenantStatus }) => updateTenantStatus(id, status),
    onSuccess: () => { invalidate(); setConfirm({ open: false, action: 'suspended', tenant: null }); setViewOpen(false); },
    onError: (err) => setMutationError(extractError(err)),
  });

  const deleteMut = useMutation({
    mutationFn: deleteTenant,
    onSuccess: () => { invalidate(); setConfirm({ open: false, action: 'delete', tenant: null }); setViewOpen(false); },
    onError: (err) => setMutationError(extractError(err)),
  });

  const rows = data?.data || [];
  const rowCount = data?.total ?? 0;

  useEffect(() => {
    if (!data || isFetching) return;
    const lastPage = Math.max(0, Math.ceil(data.total / pagination.pageSize) - 1);
    if (pagination.page > lastPage) {
      setPagination((p) => ({ ...p, page: lastPage }));
    }
  }, [data, isFetching, pagination.page, pagination.pageSize]);

  const openCreate = () => {
    setFormMode('create');
    setSelected(null);
    setFormError('');
    setFormOpen(true);
  };

  const openEdit = (tenant: TenantRow) => {
    setFormMode('edit');
    setSelected(tenant);
    setFormError('');
    setFormOpen(true);
    setViewOpen(false);
  };

  const handleFormSubmit = async (payload: TenantFormPayload) => {
    if (formMode === 'create') {
      createMut.mutate(payload);
      return;
    }

    if (!selected) return;
    setFormError('');
    try {
      await updateMut.mutateAsync({ id: selected._id, payload });
      await updateTenantModules(selected._id, buildModulesPayload(payload, selected));
      if (payload.adminUser?.email?.trim()) {
        await upsertTenantAdmin(selected._id, {
          email: payload.adminUser.email,
          firstName: payload.adminUser.firstName,
          lastName: payload.adminUser.lastName,
          phone: payload.adminUser.phone,
          password: payload.adminUser.password?.trim() || undefined,
        });
        void queryClient.invalidateQueries({ queryKey: ['tenant-admin', selected._id] });
      }
      await syncSessionIfCurrent(selected._id);
      invalidate();
      setFormOpen(false);
      setSelected(null);
    } catch (err) {
      setFormError(extractError(err));
    }
  };

  const confirmMessages = useMemo(() => ({
    suspended: 'Suspend this tenant? Users will be blocked from logging in until reactivated.',
    active: 'Activate this tenant and restore access?',
    deactivated:
      'Deactivate this tenant? Access will be blocked, and the tenant admin will receive an email/SMS explaining that the package/plan period has ended and they can no longer use the application.',
    delete:
      'This permanently soft-deletes the tenant. Data is retained but the tenant cannot be used. Type the company name below to confirm.',
  }), []);

  const openConfirm = (action: TenantStatus | 'delete', tenant: TenantRow) => {
    setViewOpen(false);
    setConfirm({ open: true, action, tenant });
  };

  const runConfirm = () => {
    if (!confirm.tenant) return;
    if (confirm.action === 'delete') deleteMut.mutate(confirm.tenant._id);
    else statusMut.mutate({ id: confirm.tenant._id, status: confirm.action });
  };

  const columns: GridColDef[] = [
    {
      field: 'companyName',
      headerName: 'Company',
      flex: 1.4,
      minWidth: 240,
      renderCell: (params) => {
        const row = params.row as TenantRow;
        const email = (row.adminEmail || '').trim();
        const phone = (row.adminPhone || '').trim();
        return (
          <Stack spacing={0.15} sx={{ py: 0.75, lineHeight: 1.25, overflow: 'hidden' }}>
            <Typography variant="body2" fontWeight={650} noWrap title={row.companyName}>
              {row.companyName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap title={email || undefined}>
              {email || 'No admin email'}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap title={phone || undefined}>
              {phone || 'No admin phone'}
            </Typography>
          </Stack>
        );
      },
    },
    { field: 'slug', headerName: 'Slug', width: 120 },
    { field: 'country', headerName: 'Country', width: 80 },
    { field: 'currency', headerName: 'Currency', width: 80 },
    { field: 'subscriptionPlan', headerName: 'Plan', width: 110 },
    {
      field: 'status', headerName: 'Status', width: 120,
      valueGetter: (_v, row) => row.status || (row.isActive ? 'active' : 'deactivated'),
      renderCell: (params) => {
        const key = params.value as TenantStatus;
        const meta = TENANT_STATUS_META[key] || TENANT_STATUS_META.active;
        return <Chip label={meta.label} color={meta.color} size="small" />;
      },
    },
    {
      field: 'modules', headerName: 'Modules', width: 100,
      renderCell: (params) => {
        const enabled = countEnabledTenantServiceModules(
          params.value as { key: string; enabled: boolean }[],
          serviceModules.map((m) => m.code),
        );
        return <Chip label={`${enabled}`} size="small" variant="outlined" />;
      },
    },
    {
      field: 'actions', headerName: 'Actions', width: 220, sortable: false,
      renderCell: (params) => {
        const tenant = params.row as TenantRow;
        const status = (tenant.status || (tenant.isActive ? 'active' : 'deactivated')) as TenantStatus;
        return (
          <Stack direction="row" spacing={0.25}>
            <Tooltip title="View"><IconButton size="small" onClick={() => { setSelected(tenant); setViewOpen(true); }}><Visibility fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(tenant)}><Edit fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Settings"><IconButton size="small" onClick={() => openModulesDialog(tenant)}><Settings fontSize="small" /></IconButton></Tooltip>
            {status === 'active' && (
              <Tooltip title="Suspend"><IconButton size="small" color="warning" onClick={() => openConfirm('suspended', tenant)}><PauseCircle fontSize="small" /></IconButton></Tooltip>
            )}
            {status === 'suspended' && (
              <Tooltip title="Activate"><IconButton size="small" color="success" onClick={() => openConfirm('active', tenant)}><PlayCircle fontSize="small" /></IconButton></Tooltip>
            )}
            {status !== 'deleted' && status !== 'deactivated' && (
              <Tooltip title="Deactivate"><IconButton size="small" onClick={() => openConfirm('deactivated', tenant)}><Block fontSize="small" /></IconButton></Tooltip>
            )}
            {status === 'deactivated' && (
              <Tooltip title="Activate"><IconButton size="small" color="success" onClick={() => openConfirm('active', tenant)}><PlayCircle fontSize="small" /></IconButton></Tooltip>
            )}
            {status !== 'deleted' && (
              <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => openConfirm('delete', tenant)}><Delete fontSize="small" /></IconButton></Tooltip>
            )}
          </Stack>
        );
      },
    },
  ];

  return (
    <PageShell>
      <PageHeader
        gradient
        eyebrow="Tenant Master"
        title="Tenants"
        subtitle="Create, configure, suspend, and manage SaaS clients with full lifecycle control."
        actions={<Button variant="contained" startIcon={<Add />} onClick={openCreate}>Create tenant</Button>}
      />

      {mutationError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMutationError('')}>{mutationError}</Alert>}
      {isError && (
        <QueryErrorAlert
          error={error}
          title="Could not load tenants"
          onRetry={() => { void refetch(); }}
        />
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search company, slug, domain…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPagination((p) => ({ ...p, page: 0 })); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 260 }}
        />
        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value as TenantStatus | 'all'); setPagination((p) => ({ ...p, page: 0 })); }}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="all">All statuses</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="suspended">Suspended</MenuItem>
          <MenuItem value="deactivated">Deactivated</MenuItem>
        </TextField>
      </Stack>

      <DataTableCard title="All tenants" subtitle="Search, filter, and manage client accounts">
        <EnhancedDataGrid
          rows={isError ? [] : rows}
          columns={columns}
          loading={isLoading || isFetching}
          getRowId={(row) => row._id}
          autoHeight
          getRowHeight={() => 'auto'}
          paginationMode="server"
          rowCount={isError ? 0 : rowCount}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          disableLocalSearch
          emptyTitle="No data found"
          emptySubtitle={
            debouncedSearch || statusFilter !== 'all'
              ? 'Try a different company name, slug, domain, or status filter'
              : 'No tenants match this view yet'
          }
          exportFileName="tenants"
          searchPlaceholder="Search tenants…"
          sx={{
            '& .MuiDataGrid-cell': { alignItems: 'flex-start' },
            '& .MuiDataGrid-cellContent': { py: 0.5 },
          }}
        />
      </DataTableCard>

      <TenantFormDrawer
        open={formOpen}
        mode={formMode}
        tenant={selected}
        loading={createMut.isPending || updateMut.isPending}
        error={formError}
        onClose={() => { setFormOpen(false); setFormError(''); }}
        onSubmit={handleFormSubmit}
      />

      <TenantViewDialog
        open={viewOpen}
        tenant={selected}
        onClose={() => setViewOpen(false)}
        onEdit={() => selected && openEdit(selected)}
        onModules={() => selected && openModulesDialog(selected)}
        onSuspend={() => selected && openConfirm('suspended', selected)}
        onActivate={() => selected && openConfirm('active', selected)}
        onDeactivate={() => selected && openConfirm('deactivated', selected)}
        onDelete={() => selected && openConfirm('delete', selected)}
      />

      <Dialog open={moduleDialog.open} onClose={closeModulesDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight={700} component="span">
            Settings — {moduleDialog.tenant?.companyName || 'Tenant'}
          </Typography>
          {moduleDialog.tenant?.slug && (
            <Typography variant="caption" color="text.secondary" display="block">
              /{moduleDialog.tenant.slug} · service modules for this tenant only
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enable or disable services for this tenant. Each option is saved only for{' '}
            <strong>{moduleDialog.tenant?.companyName || 'the selected tenant'}</strong>.
          </Typography>

          {modulesLoading && !freshModuleTenant && (
            <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">Loading tenant settings…</Typography>
            </Stack>
          )}

          {modulesError && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={<Button color="inherit" size="small" onClick={() => void refetchModules()}>Retry</Button>}
            >
              Could not refresh settings. Showing list data for this tenant.
            </Alert>
          )}

          <Grid container spacing={2}>
            {serviceModules.map((mod) => {
              const enabledMap = new Map(moduleDialog.draftModules.map((m) => [m.key, m.enabled]));
              const isEnabled = enabledMap.get(mod.code) ?? false;
              return (
                <Grid item xs={12} sm={6} key={mod.code}>
                  <Box
                    sx={{
                      height: '100%',
                      px: 1.75,
                      py: 1.5,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                    }}
                  >
                    <FormControlLabel
                      sx={{
                        m: 0,
                        width: '100%',
                        alignItems: 'flex-start',
                        gap: 1,
                        '& .MuiFormControlLabel-label': { width: '100%' },
                      }}
                      control={
                        <Switch
                          checked={isEnabled}
                          sx={{ mt: 0.25 }}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setModuleDialog((prev) => {
                              const existing = new Map(prev.draftModules.map((m) => [m.key, m.enabled]));
                              existing.set(mod.code, checked);
                              const merged = platformServices.map((m) => {
                                const isService = serviceModules.some((s) => s.code === m.code);
                                return {
                                  key: m.code,
                                  enabled: isService
                                    ? (existing.get(m.code) ?? false)
                                    : (existing.get(m.code) ?? false),
                                };
                              });
                              prev.draftModules
                                .filter((m) => !platformServices.some((p) => p.code === m.key))
                                .forEach((m) => {
                                  if (!merged.some((x) => x.key === m.key)) {
                                    merged.push({ key: m.key, enabled: m.enabled });
                                  }
                                });
                              return { ...prev, draftModules: merged };
                            });
                          }}
                        />
                      }
                      label={
                        <Box sx={{ pr: 0.5 }}>
                          <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.35 }}>
                            {mod.name}
                          </Typography>
                          {mod.description ? (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.35, lineHeight: 1.4 }}>
                              {mod.description}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.35 }}>
                              {mod.code}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </Box>
                </Grid>
              );
            })}
          </Grid>

          {!serviceModules.length && !modulesLoading && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No service modules available.
            </Typography>
          )}

          {!!serviceModules.length && (
            <>
              <Divider sx={{ my: 2.5 }} />
              <Typography variant="caption" color="text.secondary">
                {moduleDialog.draftModules.filter((m) => m.enabled && serviceModules.some((s) => s.code === m.key)).length}
                {' '}of {serviceModules.length} services enabled for this tenant
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeModulesDialog}>Cancel</Button>
          <Button
            variant="contained"
            disabled={modulesMut.isPending || !moduleDialog.tenant}
            onClick={() => moduleDialog.tenant && modulesMut.mutate({
              id: moduleDialog.tenant._id,
              modules: moduleDialog.draftModules,
            })}
          >
            {modulesMut.isPending ? 'Saving…' : 'Save settings'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirm.open}
        title={
          confirm.action === 'delete'
            ? `Delete ${confirm.tenant?.companyName || 'tenant'}?`
            : confirm.action === 'deactivated'
              ? 'Deactivate tenant'
              : `${confirm.action.charAt(0).toUpperCase()}${confirm.action.slice(1)} tenant`
        }
        message={confirmMessages[confirm.action as keyof typeof confirmMessages] || 'Continue?'}
        confirmLabel={confirm.action === 'delete' ? 'Delete permanently' : confirm.action === 'deactivated' ? 'Deactivate & notify' : 'Confirm'}
        confirmColor={confirm.action === 'delete' || confirm.action === 'deactivated' ? 'error' : confirm.action === 'suspended' ? 'warning' : 'primary'}
        loading={statusMut.isPending || deleteMut.isPending}
        verifyText={confirm.action === 'delete' ? (confirm.tenant?.companyName || '') : undefined}
        verifyLabel={
          confirm.action === 'delete' && confirm.tenant?.companyName
            ? `Type "${confirm.tenant.companyName}" to confirm delete`
            : undefined
        }
        onConfirm={runConfirm}
        onCancel={() => setConfirm({ open: false, action: 'suspended', tenant: null })}
      />
    </PageShell>
  );
}
